import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Shield, BookOpen, ClipboardCheck, FileText, Users, Scale, Lock } from 'lucide-react';

const sections = [
  {
    id: 'blueprint',
    title: 'Examination Blueprint',
    icon: BookOpen,
    content: [
      'All IMCAC examinations are designed according to a published examination blueprint that defines content coverage, cognitive levels, and assessment methods.',
      'The blueprint ensures comprehensive coverage of all competency domains within each specialty.',
      'Content validity is established through systematic mapping of examination items to competency framework outcomes.',
      'Annual review by subject matter experts ensures currency and relevance of examination content.',
      'Psychometric analysis is conducted following each examination sitting to validate item performance and overall examination reliability.',
    ]
  },
  {
    id: 'osce',
    title: 'OSCE Framework',
    icon: ClipboardCheck,
    content: [
      'OSCE stations are designed to assess clinical competency in standardised, reproducible settings.',
      'Each station includes candidate instructions, examiner marking guides, standardised patient briefs (where applicable), and equipment checklists.',
      'Scoring rubrics use global rating scales supplemented by task-specific checklists.',
      'All examiners undergo calibration training prior to each examination sitting.',
      'Station validity and reliability are monitored through post-examination psychometric analysis.',
      'OSCE blueprints are published in advance to ensure transparency of assessment scope.',
    ]
  },
  {
    id: 'caselog',
    title: 'Case Log Requirements',
    icon: FileText,
    content: [
      'All candidates must submit a verified clinical case log as part of the certification process.',
      'Case logs must document a minimum number of procedures across specified categories relevant to the examination specialty.',
      'Each entry must include: anonymised patient demographics, procedure type, diagnosis, treatment plan, products/devices used, complications (if any), management protocols, and outcomes.',
      'Case logs must be validated by a supervisor or senior practitioner.',
      'IMCAC reserves the right to audit any case log submission for accuracy and compliance.',
      'Falsification of case log entries will result in immediate disqualification and potential reporting to relevant regulatory authorities.',
    ]
  },
  {
    id: 'recertification',
    title: 'Recertification & CPD',
    icon: Shield,
    content: [
      'Recertification is required every five years to ensure ongoing competence.',
      'Candidates must complete a minimum of 25 CPD hours per year (125 hours per cycle) in relevant activities.',
      'Approved CPD activities include conference attendance, peer-reviewed publications, clinical audits, structured learning modules, and teaching activities.',
      'CPD portfolios must be submitted for audit during the recertification year.',
      'The IMCAC CPD Committee reviews all submissions and may request additional evidence.',
      'Non-compliance with CPD requirements may result in suspension of certification status.',
    ]
  },
  {
    id: 'examiner',
    title: 'Examiner Accreditation',
    icon: Users,
    content: [
      'All IMCAC examiners must hold relevant specialist qualifications and active registration in their jurisdiction.',
      'Examiners undergo a structured selection process including background verification, competency assessment, and interview.',
      'New examiners complete a mandatory training programme covering examination standards, marking calibration, and candidate interaction protocols.',
      'Regular calibration sessions are conducted to maintain scoring consistency.',
      'Examiners must declare any conflicts of interest and recuse themselves from examining known candidates.',
      'Examiner performance is monitored and audited through post-examination feedback and statistical analysis.',
    ]
  },
  {
    id: 'ethics',
    title: 'Ethics & Conflict of Interest',
    icon: Scale,
    content: [
      'All IMCAC officers, examiners, and board members must comply with the IMCAC Code of Ethics.',
      'Annual conflict of interest declarations are required from all individuals involved in examination development, delivery, or governance.',
      'Conflicted individuals must recuse themselves from relevant decisions or examination activities.',
      'IMCAC maintains strict separation between training providers and the examination authority.',
      'No individual or institution involved in delivering IMCAC-accredited training programmes may participate in the assessment of their own graduates.',
      'Violations of ethics policies may result in removal from role and referral to relevant professional bodies.',
    ]
  },
  {
    id: 'appeals',
    title: 'Appeals & Review',
    icon: Scale,
    content: [
      'Candidates may submit a formal appeal within 14 days of receiving examination results.',
      'Appeals must state specific grounds (procedural irregularity, extenuating circumstances, or administrative error).',
      'Appeals are reviewed by an independent Appeals Committee with no involvement in the original examination.',
      'The Appeals Committee may uphold, modify, or overturn the original decision.',
      'Decisions are communicated in writing within 30 days.',
      'Where warranted, a personal hearing may be offered to the candidate.',
      'The Appeals Committee decision is final and binding.',
    ]
  },
  {
    id: 'data',
    title: 'Data Confidentiality & Integrity',
    icon: Lock,
    content: [
      'IMCAC follows GDPR and applicable Indian data protection legislation.',
      'All candidate data is collected, processed, and stored securely with encryption.',
      'Access to personal data is restricted to authorised personnel only.',
      'Candidates have the right to access, correct, and request deletion of their personal data.',
      'Examination content is classified as confidential and protected under intellectual property provisions.',
      'All staff handling personal or examination data undergo regular data protection training.',
      'Data breaches are reported in accordance with applicable regulatory requirements.',
    ]
  },
];

export default function GovernancePage() {
  return (
    <div data-testid="governance-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Regulatory Framework</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            Governance & Regulatory Framework
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
        </div>
      </section>

      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <Tabs defaultValue="blueprint" className="w-full">
            <TabsList className="flex flex-wrap gap-1 h-auto bg-[#F1F5F9] p-1 mb-8" data-testid="governance-tabs">
              {sections.map((s) => (
                <TabsTrigger key={s.id} value={s.id} className="text-xs px-3 py-2 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid={`gov-tab-${s.id}`}>
                  {s.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {sections.map((section) => (
              <TabsContent key={section.id} value={section.id}>
                <Card className="imcac-card" data-testid={`gov-content-${section.id}`}>
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center">
                        <section.icon className="h-5 w-5 text-[#C5A572]" />
                      </div>
                      <h2 className="text-2xl font-semibold text-[#1B2A4A] font-heading">{section.title}</h2>
                    </div>
                    <ul className="space-y-4">
                      {section.content.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-[#F1F5F9] border border-slate-200 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-bold text-[#1B2A4A]">{i + 1}</span>
                          </span>
                          <p className="text-sm text-slate-700 leading-relaxed">{point}</p>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>
    </div>
  );
}
